<script lang="ts">
    import PlaybackItem from '~/components/structure/Players/components/MobileNowPlaying/PlaybackItem.svelte';
    import { getI18n } from '~/stores/i18n';
    import type { QueueItemMetadata } from '~/jet/models';
    import { createEventDispatcher } from 'svelte';
    import NowPlayingInfoData from './NowPlayingInfoData.svelte';

    const panelId = 'info';
    const dispatch = createEventDispatcher();
    const i18n = getI18n();

    // Now Playing Item to show at top of queue in Mobile view
    export let item: QueueItemMetadata | null;

    // Element to tell context menu where to render menu options
    // Only used by PlaybackItem in mobile view
    export let parentElement: HTMLElement = null;

    // Flag to modify how the queue is displayed
    export let isMobileView: boolean = false;
    export let isEmpty: boolean = true;
    export let emptyInfoText: string | null = null;
</script>

<div
    data-testid="now-playing-info"
    class="now-playing-info"
    class:now-playing-info--desktop={!isMobileView}
>
    <!-- Now Playing item controls -->
    {#if item && isMobileView}
        <div class="now-playing" data-testid="now-playing-playback-item">
            <PlaybackItem
                layout="compact"
                {item}
                on:artworkClick
                bind:parentElement
                artworkButtonAriaLabel={$i18n.t(
                    'ASE.Web.MusicClassical.Controls.Expand',
                )}
            />
        </div>
    {/if}

    <div class="info-section" data-testid="info-section">
        {#if !isEmpty}
            <!-- Now Playing Info -->
            <NowPlayingInfoData {item} />
        {:else}
            <!-- Empty info message -->
            <div class="info-empty" data-testid="info-empty">
                <p class="info-empty-message">
                    {emptyInfoText}
                </p>
            </div>
        {/if}
    </div>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '../../../../../styles/music-classical/core/mixins/balance-line-break'
        as *;

    $now-playing-item-height: 66px;
    $bottom-fade-height: 70px;

    // TODO: Improve this through slots?
    // rdar://110174109 ([Mobile Queue Parity] Improve mobile queue through slots)
    .now-playing-info {
        height: 100%;

        :global(.credits-list) {
            --credits-list-padding: 0;

            @media (--sidebar-visible) {
                --credits-list-grid-cols: repeat(2, 1fr);
            }
        }

        :global(.credits-list__title) {
            --credits-title-color: var(--systemSecondary-onDark);
        }

        :global(.credits-list__subtitle) {
            --credits-subtitle-color: var(--systemPrimary-onDark);
            --credits-chevron-color: var(--systemPrimary-onDark);

            // 1290px - approx breakpoint where panel container becomes too narrow
            // causing chevron to wrap onto individual line
            @media (--sidebar-visible) and (width <= 1290px) {
                @include balance-line-break;
            }
        }
    }

    .now-playing-info--desktop {
        .info-section {
            height: inherit;
        }
    }

    .now-playing,
    .info-section {
        padding: 0 32px;
    }

    .now-playing {
        padding-bottom: 32px;
    }

    .info-section {
        // Use Flexbox to allow us to render the container elements in a different order.
        // This helps smoothly scroll the sticky header out of view.
        display: flex;
        flex-direction: column;
        padding-bottom: $bottom-fade-height; // add extra scroll room for last item in list to scroll above fade effect
        height: calc(
            100% - 72px
        ); // info list height minus info list header height
        overflow-y: auto;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    .info-empty {
        // in order to center the message without making a scroll container, need to subtract the height of the now playing item
        height: calc(100% - #{$now-playing-item-height});
        display: flex;
        align-items: center;
        justify-content: center;
        font: var(--body);
        color: var(--systemSecondary-onDark);
    }

    .info-empty-message {
        padding: 0 32px;
        text-align: center;
    }
</style>
